<template>
  <Head :title="$page.props.app_name + (title ? ' - ' + title : '')">
    <link :href="$page.props.favicon_url" rel="icon" :type="$page.props.favicon_type" />
  </Head>
  <slot name="outside" />
  <aside
    id="forgeron-menu"
    :class="statusClass('left', $page.props.off_canvas_elements?.left)"
    class="forgeron-aside forgeron-left offcanvas-menu"
  >
    <slot name="aside-left">
      <header class="forgeron-header">
        <slot name="header-left">
          <div class="d-flex align-items-center h-100">
            <a href="#" class="d-block w-100 text-center">
              <img :src="$page.props.app_logo" class="app-logo" />
            </a>
          </div>
        </slot>
      </header>
      <main class="forgeron-main">
        <slot name="main-left">
          <main-navigation :links="$page.props.aspectMenuItems"></main-navigation>
        </slot>
      </main>
      <footer class="row forgeron-footer">
        <slot name="footer-left">
          <div class="row">
            <div class="col-lg-12">
              <a v-if="$page.props.user.roles.includes('tech')" :href="route(`settings.access.index`)">
                <i class="fa fa-cog"></i>
              </a>
            </div>
          </div>
        </slot>
      </footer>
    </slot>
  </aside>
  <main
    :id="pageId()"
    class="forgeron-main forgeron-center"
    :class="{
      'open-left': statusClass('left', $page.props.off_canvas_elements?.left),
      'open-right': statusClass('right', $page.props.off_canvas_elements?.right),
    }"
  >
    <header class="forgeron-header d-flex justify-content-between align-items-center">
      <slot name="header-main">
        <div
          v-if="$page.props.off_canvas_elements?.left?.active"
          :class="$page.props.off_canvas_elements?.left?.fixed ? 'invisible' : ''"
          class="d-inline-block menu-icon cursor-pointer m-4 flex-grow-1"
          @click="toggleMenuOpen('left')"
        >
          <i class="fas fa-bars fa-2x" style="cursor: pointer"></i>
        </div>
        <div>
          <component :is="customActionBox(aspect)"></component>
          <Link
            v-if="$page.props.user.roles.includes('tech')"
            :href="route(`admin.resources.portals.setup`)"
          >
            <i class="fa-solid fa-archway h2"></i>
          </Link>
          <div class="d-inline-block menu-icon cursor-pointer m-4" v-if="$page.props.currentTeam">
            <team-menu></team-menu>
          </div>
          <div class="d-inline-block menu-icon cursor-pointer m-4">
            <user-menu></user-menu>
          </div>
        </div>
        <div
          v-if="$page.props.off_canvas_elements?.right?.active"
          :class="$page.props.off_canvas_elements?.right?.fixed ? 'invisible' : ''"
          class="d-inline-block menu-icon cursor-pointer m-4"
          @click="toggleMenuOpen('right')"
        >
          <i class="fas fa-bars fa-2x" style="cursor: pointer"></i>
        </div>
      </slot>
    </header>
    <div class="forgeron-main-content" :class="{ 'admin-content': $page.props.currentAspect == 'admin' }">
      <div class="forgeron-content-container">
        <div class="toast-container">
          <toasts v-if="toasts || errors" :toasts="getAllToasts(toasts, errors)"></toasts>
        </div>
        <slot name="above">
          <slot name="breadcrumbs">
            <breadcrumbs />
          </slot>
        </slot>
        <div class="forgeron-page-content">
          <div class="forgeron-page-aside forgeron-page-content-left">
            <slot name="left" />
          </div>
          <div class="forgeron-page-content-center flex-grow-1">
            <slot name="top" />
            <slot />
            <slot name="bottom" />
          </div>
          <div class="forgeron-page-aside forgeron-page-content-right">
            <slot name="right"></slot>
          </div>
        </div>
        <slot name="below" />
      </div>
    </div>
    <footer class="forgeron-footer">
      <slot name="footer-main"></slot>
    </footer>
  </main>
  <aside
    :class="statusClass('right', $page.props.off_canvas_elements?.right)"
    class="forgeron-aside forgeron-right offcanvas-menu"
  >
    <slot name="aside-right">
      <header class="forgeron-header">
        <slot name="header-right"></slot>
      </header>
      <main class="forgeron-main">
        <slot name="main-right"></slot>
      </main>
      <footer class="row forgeron-footer">
        <slot name="footer-right"></slot>
      </footer>
    </slot>
  </aside>
</template>

<script>
import merge from 'lodash/merge';
import { onMounted, ref } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';

import AdminSidebox from '@kanrisha/components/Layout/AdminSidebox';
import Breadcrumbs from '@kanrisha/components/Layout/Breadcrumbs';
import MainNavigation from '@kanrisha/components/Navigation';
import TeamMenu from '@kanrisha/components/Layout/TeamMenu';
import Toasts from '@kanrisha/components/Base/Toasts';
import UserMenu from '@kanrisha/components/Layout/UserMenu';

import useCustomActionBox from '@kanrisha/composables/useCustomActionBox';
import useTheme from '@kanrisha/composables/useTheme';

export default {
  components: {
    Breadcrumbs,
    TeamMenu,
    AdminSidebox,
    Head,
    Link,
    MainNavigation,
    Toasts,
    UserMenu,
  },
  props: {
    toasts: { type: Object, default: () => {} },
    errors: { type: Object, default: () => {} },
    title: { type: String, default: null },
  },
  setup() {
    const { customActionBox } = useCustomActionBox(null, 'Global');
    const { adaptTheme } = useTheme();

    const getAllToasts = (toasts, errors) => {
      let errorToasts = [];
      for (let key in errors) {
        errorToasts.push({
          title: 'Fehler',
          message: errors[key],
          type: 'error',
        });
      }
      return merge(toasts, errorToasts.values());
    };

    const logout = (event) => {
      event.preventDefault();
      router.post('/logout', {}, { onSuccess: (window.location.href = '/') });
    };

    const elementIsOpen = ref({ left: null, right: null });

    const statusClass = (name, settings) => {
      if (settings?.fixed) {
        return settings.open ? 'open' : '';
      }
      if (elementIsOpen.value[name] === null) {
        elementIsOpen.value[name] = settings?.open;
      }

      return elementIsOpen.value[name] ? 'open' : '';
    };

    const toggleMenuOpen = (name) => {
      elementIsOpen.value[name] = !elementIsOpen.value[name];
    };

    const pageId = () => {
      let urlParts = (window.location + '').split('/');

      return urlParts[3] + '_' + urlParts[4];
    };

    onMounted(() => {
      adaptTheme();
    });

    return {
      adaptTheme,
      customActionBox,
      elementIsOpen,
      getAllToasts,
      logout,
      pageId,
      statusClass,
      toggleMenuOpen,
    };
  },
};
</script>
